import { ApiError } from '@/lib/api';
import type { CoachingAIJobProgress, CoachingAIJobResult, Player, ValidatedCoachingClaimCitation, ValidatedCoachingEvidenceOutput } from '@/lib/types';

export function isRunning(job: CoachingAIJobProgress | undefined): boolean {
  return job?.status === 'queued' || job?.status === 'processing';
}
export function resultOutput(result: CoachingAIJobResult | undefined): ValidatedCoachingEvidenceOutput | null {
  if (!result) return null;
  if ('output' in result) return result.output.schema_version === 'coaching-validated-output.v2' ? result.output : null;
  return result.draft.schema_version === 'coaching-draft.v3' ? result.draft.structured_output : null;
}
export function safeAssistMessage(error: unknown): string {
  const code = error instanceof ApiError && typeof error.detail === 'object' && error.detail !== null && 'code' in error.detail
    ? String(error.detail.code) : '';
  return codeMessage(code);
}
export function codeMessage(code: string): string {
  if (['quota_exceeded', 'external_allocation_unavailable', 'occupancy_unavailable', 'dispatch_unavailable'].includes(code)) return 'Preparation is at its current limit. You can continue manually.';
  if (['ai_processing_blocked', 'drafting_disabled', 'policy_changed', 'policy_unavailable', 'coaching_access_required'].includes(code)) return 'AI preparation is not available for this context. Manual capture and focus selection remain available when you have coaching access.';
  if (['dispatch_unknown', 'provider_timeout', 'accounting_review_required'].includes(code)) return 'Preparation could not be confirmed. Continue manually; a new generation is available only when the service allows it.';
  if (['evidence_stale', 'evidence_unavailable', 'job_expired', 'job_unavailable', 'result_unavailable', 'revision_conflict', 'evidence_retry_required', 'database_retry_required', 'context_unavailable', 'record_expired', 'record_unavailable'].includes(code)) return 'This context changed or is unavailable. Review the current sources before continuing.';
  return 'Preparation could not complete. Your typed work is still here; you can continue manually.';
}
export function jobMessage(job: CoachingAIJobProgress): string {
  switch (job.status) {
    case 'queued': return 'Your preparation is queued…';
    case 'processing': return 'Preparing your private brief…';
    case 'needs_review': return job.result_available ? 'Your private brief is ready to review.' : 'The brief is unavailable. Continue manually.';
    case 'cancelled': return 'Preparation cancelled. Your typed work is still here.';
    case 'blocked': case 'failed': return codeMessage(job.code);
  }
}
export function citationDate(citation: ValidatedCoachingClaimCitation): string {
  const value = citation.occurrence_precision === 'current_state' ? citation.baseline_as_of : citation.occurred_at;
  if (!value || citation.occurrence_precision === 'unknown') return 'Occurrence date unknown';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Occurrence date unknown';
  // Day precision never invents a time or shifts a calendar day through timezone conversion.
  if (citation.occurrence_precision === 'day') return `${value.slice(0, 10)} · day precision`;
  return `${date.toLocaleString()}${citation.occurrence_precision === 'current_state' ? ' · current state as of' : ''}`;
}
export function provenanceLabel(citation: ValidatedCoachingClaimCitation): string {
  const relation: Record<string, string> = { guardian: 'Guardian', player: 'Player', self: 'Player', coach: 'Coach', assistant_coach: 'Assistant coach' };
  const actor = relation[citation.relationship] ?? 'Attributed source';
  switch (citation.provenance) {
    case 'reported': return `${actor} report · not a coach-observed fact`;
    case 'observed': return `${actor} observation · recorded fact, not proof of improvement`;
    case 'interpreted': return 'Interpretation · not an established fact';
    case 'cue': return 'Coaching cue · not an observed outcome';
    case 'current_state': return 'Recorded current state · not evidence of improvement';
  }
}
/** Resolve only this authorized roster. Proof metadata is never a name fallback. */
export function authorizedLabels(ids: string[], players: Player[]): string[] | null {
  const names = new Map(players.map(p => [p.id, p.display_name]));
  return ids.every(id => names.has(id)) ? ids.map(id => names.get(id)!) : null;
}
export function displayText(text: string, output: ValidatedCoachingEvidenceOutput, players: Player[]): string {
  const labels = new Map<string, string>();
  const citations = output.observations.flatMap(o => o.citations);
  for (const citation of citations) {
    for (const id of [citation.actor_user_id, citation.source_id]) {
      if (id && !/^\d+$/.test(id)) labels.set(id, 'Source');
    }
  }
  for (const id of new Set([...output.player_ids, ...output.observations.flatMap(o => o.subject_player_ids), ...players.map(p => p.id)])) {
    if (!/^\d+$/.test(id)) labels.set(id, players.find(p => p.id === id)?.display_name ?? 'Player unavailable');
  }
  if (!labels.size) return text;
  const escaped = [...labels.keys()].sort((a, b) => b.length - a.length).map(id => id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const opaque = '(?:player|actor|user|coach|team|club|capture|run|job|focus|event|note|goal)-[A-Za-z0-9_-]+|[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}';
  const pattern = new RegExp(`(^|[^A-Za-z0-9_-])(${escaped.join('|')}|${opaque})(?=$|[^A-Za-z0-9_-])`, 'g');
  // One pass: newly inserted names are never scanned as identifiers. Numeric
  // proof IDs are never replaced in dates, scores, counts or ordinary prose.
  return text.replace(pattern, (_match, prefix: string, id: string) => `${prefix}${labels.get(id) ?? (id.startsWith('player-') ? 'Player unavailable' : 'Source')}`);
}
export function priorityContext(output: ValidatedCoachingEvidenceOutput, priority: ValidatedCoachingEvidenceOutput['priorities'][number]): string {
  const observations = output.observations.filter(o => priority.observation_ids.includes(o.id) || output.hypotheses.some(h => priority.hypothesis_ids.includes(h.id) && h.observation_ids.includes(o.id)));
  if (observations.some(o => o.citations.some(c => c.provenance === 'reported'))) return 'Based on a report · confirm through observation';
  if (priority.hypothesis_ids.length || observations.some(o => o.citations.some(c => c.provenance === 'interpreted'))) return 'Hypothesis · explore, do not treat as fact';
  if (observations.some(o => o.citations.some(c => c.provenance === 'cue'))) return 'Coaching suggestion · outcome not yet observed';
  return 'Suggestion · coach review required';
}
